import React, { useEffect } from 'react';

export interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const DEFAULT_TITLE = 'BARTECH | Hardware para Retail, Bodegas, Scanners, RFID y POS en Chile';
const DEFAULT_DESCRIPTION =
  'Distribuidor B2B líder en Chile de hardware tecnológico de alta gama para retail, bodegas e industria. Capturadores PDA Honeywell, Scanners 1D/2D, Robots con IA, RFID y POS. Cotizaciones y soporte técnico oficial.';
const DEFAULT_IMAGE = 'https://bartech.cl/bartech_logo.jpg';
const BASE_URL = 'https://bartech.cl';

export const SeoHead: React.FC<SeoProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  jsonLd,
}) => {
  useEffect(() => {
    // 1. Título de página
    const fullTitle = title
      ? `${title} | BARTECH Chile`
      : DEFAULT_TITLE;
    document.title = fullTitle;

    // Helper para actualizar o crear meta tags
    const updateMetaTag = (selector: string, attributeName: string, value: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        if (selector.startsWith('meta[name=')) {
          const nameMatch = selector.match(/name="([^"]+)"/);
          if (nameMatch) element.setAttribute('name', nameMatch[1]);
        } else if (selector.startsWith('meta[property=')) {
          const propMatch = selector.match(/property="([^"]+)"/);
          if (propMatch) element.setAttribute('property', propMatch[1]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attributeName, value);
    };

    // 2. Metas estándar
    updateMetaTag('meta[name="description"]', 'content', description);
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'content', keywords);
    }
    updateMetaTag('meta[name="robots"]', 'content', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');

    // 3. Open Graph
    const currentUrl = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : BASE_URL);
    updateMetaTag('meta[property="og:title"]', 'content', fullTitle);
    updateMetaTag('meta[property="og:description"]', 'content', description);
    updateMetaTag('meta[property="og:url"]', 'content', currentUrl);
    updateMetaTag('meta[property="og:type"]', 'content', ogType);
    updateMetaTag('meta[property="og:image"]', 'content', ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`);

    // 4. Twitter Card
    updateMetaTag('meta[name="twitter:title"]', 'content', fullTitle);
    updateMetaTag('meta[name="twitter:description"]', 'content', description);
    updateMetaTag('meta[name="twitter:image"]', 'content', ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`);

    // 5. Canonical Link
    let canonicalTag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', currentUrl);

    // 6. Structured Data (Schema.org JSON-LD)
    const scriptId = 'bartech-seo-jsonld';
    let scriptTag = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (jsonLd) {
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = scriptId;
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(jsonLd);
    } else if (scriptTag) {
      scriptTag.remove();
    }

    return () => {
      // Limpieza de schema al desmontar la vista
      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, noIndex, jsonLd]);

  return null;
};
